import { BitcoinAddressHelper, GelatoTransactionSender, OrangeKitSdk, } from "@orangekit/sdk";
import { createConnector, normalizeChainId } from "wagmi";
import { getAddress } from "viem";
import { OrangeKitEthersSigner } from "./ethers-signer";
import { createOrangeKitClient } from "./client";
import { OrangeKitBitcoinWalletConnectionError, WalletNetworkDoesNotMatchProviderChainError, } from "./errors";
export function createOrangeKitConnector(id, name, icon, rpcUrl, initialChainId, bitcoinWalletProvider, relayApiKey) {
    let sdk;
    let ethersSigner;
    if (relayApiKey === "")
        throw new Error("Relay API key is required");
    return createConnector(({ chains, emitter }) => ({
        id,
        uid: id,
        name,
        type: "orangekit",
        icon,
        async setup() {
            // We can reinitialize the sdk on `switchChain`.
            sdk = await OrangeKitSdk.init(initialChainId, rpcUrl, new GelatoTransactionSender(relayApiKey));
        },
        async connect({ chainId }) {
            try {
                if (!bitcoinWalletProvider.isInstalled())
                    throw new Error(`${name} wallet is not installed`);
                // Ask for permission.
                const isChainSupported = chains.some((chain) => chain.id === chainId);
                const providerChainId = await this.getChainId();
                if (!isChainSupported || providerChainId !== chainId)
                    throw new Error("Unsupported network ");
                await bitcoinWalletProvider.connect();
                const btcAddress = await bitcoinWalletProvider.getAddress();
                const doesBitcoinAddressNetworkMatchWithProviderChain = providerChainId === 1
                    ? BitcoinAddressHelper.isMainnetAddress
                    : BitcoinAddressHelper.isTestnetAddress;
                if (btcAddress &&
                    !doesBitcoinAddressNetworkMatchWithProviderChain(btcAddress)) {
                    throw new WalletNetworkDoesNotMatchProviderChainError();
                }
                const accounts = await this.getAccounts();
                const [account] = accounts;
                ethersSigner = OrangeKitEthersSigner.init(null, account, bitcoinWalletProvider, sdk);
                return {
                    chainId: providerChainId,
                    accounts,
                };
            }
            catch (err) {
                const error = new OrangeKitBitcoinWalletConnectionError(name, err);
                emitter.emit("error", { error });
                throw error;
            }
        },
        disconnect() {
            return bitcoinWalletProvider.disconnect();
        },
        async getAccounts() {
            const bitcoinAddress = await this.getBitcoinAddress();
            const publicKey = await bitcoinWalletProvider.getPublicKey();
            const ethAddress = await sdk.predictAddress(bitcoinAddress, publicKey);
            return [getAddress(ethAddress)];
        },
        async getProvider(parameters) {
            let selectedChainId = initialChainId;
            if (parameters && "chainId" in parameters && parameters.chainId) {
                selectedChainId = parameters.chainId;
            }
            // TODO: What should we return here? We have custom client for this
            // connector so the custom client will be always used as provider in
            // wagmi.
            if (!sdk || selectedChainId !== initialChainId)
                sdk = await OrangeKitSdk.init(selectedChainId, rpcUrl, new GelatoTransactionSender(relayApiKey));
            return Promise.resolve(sdk);
        },
        async getChainId() {
            const provider = await this.getProvider();
            return normalizeChainId(provider.chainId);
        },
        async isAuthorized() {
            if (!bitcoinWalletProvider.isInstalled())
                return false;
            try {
                const account = await bitcoinWalletProvider?.getAddress();
                return !!account;
            }
            catch (error) {
                return false;
            }
        },
        async getClient() {
            const chainId = await this.getChainId();
            const [account] = await this.getAccounts();
            return createOrangeKitClient({
                chainId,
                bitcoinWalletProvider,
                rpcUrl,
                address: account,
                sdk,
            });
        },
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        onAccountsChanged(accounts) { },
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        onChainChanged(chainId) { },
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        onConnect(connectInfo) { },
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        onDisconnect(error) { },
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        onMessage(message) { },
        toEthersSigner(provider) {
            return ethersSigner.connect(provider);
        },
        getBitcoinAddress() {
            return bitcoinWalletProvider.getAddress();
        },
        getBitcoinProvider() {
            return bitcoinWalletProvider;
        },
    }));
}
//# sourceMappingURL=connector.js.map